import { ChangeDetectionStrategy, Component, inject, input, OnInit, output } from "@angular/core";
import {
  AbstractControl,
  FormsModule,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { MatButton } from "@angular/material/button";
import { MatInput, MatLabel, MatSuffix } from "@angular/material/input";
import { TranslocoDirective } from "@jsverse/transloco";
import { MatButtonToggle, MatButtonToggleGroup } from "@angular/material/button-toggle";
import { MatError, MatFormField } from "@angular/material/form-field";
import { CustomerInfo } from "../portal.store";
import { ActivatedRoute } from "@angular/router";
import { TranslocoDecimalPipe } from "@jsverse/transloco-locale";
import { ConfigAppService } from "@tenzu/repository/config-app/config-app.service";

@Component({
  selector: "app-tenzu-front-portal-form",
  imports: [
    FormsModule,
    MatButton,
    MatButtonToggle,
    MatButtonToggleGroup,
    MatError,
    MatFormField,
    MatInput,
    MatLabel,
    MatSuffix,
    TranslocoDirective,
    ReactiveFormsModule,
    TranslocoDecimalPipe,
  ],
  template: ` @let customer = customerInfo();
    @let priceAmount = form.controls.priceAmount;
    <ng-container *transloco="let t">
      <h2 class="mat-headline-small text-tertiary text-center">
        {{ t("portal.modal.right_panel.title") }}
      </h2>
      <form [formGroup]="form" (ngSubmit)="submitForm.emit(form.getRawValue())" class="flex flex-col gap-y-5">
        <section class="flex justify-center">
          <mat-button-toggle-group formControlName="recurrent" [attr.aria-label]="t('commons.select_option')">
            <mat-button-toggle [value]="false">
              {{ t("portal.modal.right_panel.priceAmount.input.value.one_shot") }}
            </mat-button-toggle>
            <mat-button-toggle [value]="true">
              {{ t("portal.modal.right_panel.priceAmount.input.value.recurrent") }}
            </mat-button-toggle>
          </mat-button-toggle-group>
        </section>
        <mat-form-field>
          <mat-label>{{ t("portal.modal.right_panel.priceAmount.input.title") }}</mat-label>
          <input
            type="number"
            matInput
            formControlName="priceAmount"
            placeholder="{{ t('portal.modal.right_panel.priceAmount.input.placeholder') }}"
          />
          <span matSuffix>€ &nbsp;</span>
          @if (priceAmount.hasError("required")) {
            <mat-error [innerHTML]="t('portal.modal.right_panel.priceAmount.error.required')"></mat-error>
          } @else if (priceAmount.hasError("minAmount")) {
            <mat-error
              [innerHTML]="
                t('portal.modal.right_panel.priceAmount.error.minAmount', { min: priceAmount.getError('minAmount') })
              "
            ></mat-error>
          } @else if (priceAmount.hasError("wholeNumber")) {
            <mat-error [innerHTML]="t('portal.modal.right_panel.priceAmount.error.wholeNumber')"></mat-error>
          } @else if (priceAmount.hasError("max")) {
            <mat-error
              [innerHTML]="
                t('portal.modal.right_panel.priceAmount.error.max', {
                  max: (priceAmount.getError('max')?.max | translocoDecimal),
                })
              "
            ></mat-error>
          }
        </mat-form-field>
        <p class="mat-body-small" [innerHTML]="t('portal.modal.right_panel.explanation_1')"></p>
        @if (priceAmount.value === 0) {
          <button type="button" mat-flat-button class="error-button text-nowrap" (click)="ignoreModal.emit()">
            {{ t("portal.modal.cancel") }}
          </button>
        } @else if (form.value.recurrent && customer?.activeSubscription) {
          <button type="button" mat-flat-button class="tertiary-button text-nowrap" (click)="goToCustomerPortal.emit()">
            {{ t("portal.modal.right_panel.configure") }}
          </button>
        } @else {
          <button type="submit" mat-flat-button class="primary-button text-nowrap" [disabled]="form.invalid">
            {{ t("portal.modal.right_panel.submit") }}
          </button>
        }
      </form>
      @let configLegal = configAppService.configLegal();
      <p
        class="mat-body-small flex flex-col gap-2"
        [innerHTML]="
          t('portal.modal.right_panel.explanation_2', {
            companyRegister: configLegal?.companyRegister,
            companyNumber: configLegal?.companyNumber,
            companyAddress: configLegal?.companyAddress,
            privacyPolicyLink: configLegal?.privacy,
            email: configLegal?.contactEmail,
          })
        "
      ></p>
      <p class="mat-body-small flex flex-col gap-2">
        <a target="_blank" [href]="configLegal?.tos" [innerHTML]="t('portal.modal.right_panel.tos_link_text')"></a>
      </p>
    </ng-container>`,
  styles: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PortalFormComponent implements OnInit {
  configAppService = inject(ConfigAppService);

  customerInfo = input<CustomerInfo>();
  goToCustomerPortal = output<void>();
  ignoreModal = output<void>();
  priceAmount = output<number>();
  submitForm = output<{ priceAmount: number; recurrent: boolean }>();
  route = inject(ActivatedRoute);
  fb = inject(NonNullableFormBuilder);
  form = this.fb.group(
    {
      priceAmount: [5, [Validators.required, this.zeroOrMinValidator(0.5), Validators.max(999999.99)]],
      recurrent: [false],
    },
    { validators: [this.wholeNumberCheck] },
  );

  zeroOrMinValidator(minAmount: number): ValidatorFn {
    return (control: AbstractControl<number>) => {
      if (control.value === 0) {
        return null;
      }
      return control.value < minAmount ? { minAmount } : null;
    };
  }

  ngOnInit(): void {
    this.setupFormFromQueryParams();
    this.form.controls.priceAmount.valueChanges.subscribe((value) => {
      this.priceAmount.emit(value);
    });
  }

  wholeNumberCheck(control: AbstractControl) {
    const recurrent = control.get("recurrent");
    const priceAmount = control.get("priceAmount");
    if (!recurrent || !priceAmount) {
      throw new Error("Invalid form");
    }
    priceAmount.updateValueAndValidity({ onlySelf: true });
    if (recurrent.value && (priceAmount.value as number) % 1 != 0) {
      priceAmount.setErrors({ wholeNumber: true });
    }
    return null;
  }

  setupFormFromQueryParams() {
    const recurrentParam = this.route.snapshot.queryParamMap.get("recurrent");
    const priceAmountParam = this.route.snapshot.queryParamMap.get("priceAmount");
    if (recurrentParam && recurrentParam.toLowerCase() === "true") {
      this.form.controls.recurrent.setValue(true);
    }
    if (priceAmountParam) {
      const priceAmount = parseInt(priceAmountParam, 10);
      if (!Number.isNaN(priceAmount)) {
        this.form.controls.priceAmount.setValue(priceAmount / 100);
      }
    }
  }
}
